import React from "react";
import { FiPlay } from "react-icons/fi";
import Link from "next/link";

import {
  generatePostImageThumbUrl,
  generatePostVideoThumbnailUrl,
  abbreviateNumber,
  generatePostDrawingUrl,
  calculateFontSizeForTextContent,
} from "@utility/index";
import { Orientation, ContentType } from "@utility/constants";

function PostOverlay({ post }) {
  return (
    <span className="text-shadow-text text-white absolute bottom-2 left-2 flex items-center z-10">
      <FiPlay className="mr-2 text-xl" /> {abbreviateNumber(post.viewCount)}
    </span>
  );
}

// Images taken in v1.9 will actually have landscape dimensions
// Let's rotate them to have viewing consistency across platforms/clients
const imageRotation = {
  [Orientation.LANDSCAPE_LEFT]: 90,
  [Orientation.LANDSCAPE_RIGHT]: -90,
};

export default function FeedCard({ post }) {
  const rotate = imageRotation[post.contentOrientation];

  // Render Image Post
  const renderImageContent = () => {
    const postThumbnail = generatePostImageThumbUrl({
      post,
      edits: {
        ...(rotate ? { rotate } : undefined),
        resize: { width: 340, fit: "cover", strategy: "attention" },
      },
    });
    return (
      <img
        className="h-full w-full object-cover"
        src={postThumbnail}
        alt={post?.title}
      />
    );
  };
  // Render Video Post
  const renderVideoContent = () => {
    const postThumbnail = generatePostVideoThumbnailUrl({ post });
    return (
      <img
        className="h-full w-full object-cover"
        src={postThumbnail}
        alt={post?.title}
      />
    );
  };
  // Render Drawing Post
  const renderDrawingContent = () => {
    const postThumbnail = generatePostDrawingUrl({ post });
    return (
      <img
        className="h-full w-full object-cover"
        src={postThumbnail}
        alt={post?.title}
      />
    );
  };

  const fontSize = calculateFontSizeForTextContent({
    textLength: post.text?.length,
    maxFontSize: 20.5,
  });
  // Render Text Post
  const renderTextContent = () => (
    <div className="flex h-full w-full bg-[#0f0e12] px-2">
      <p
        className="max-w-full m-auto break-words text-[#edecf2]"
        style={{ fontSize }}
      >
        {post.text}
      </p>
    </div>
  );

  const renderContent = () => {
    switch (post.contentType) {
      case ContentType.IMAGE:
        return renderImageContent();

      case ContentType.VIDEO:
        return renderVideoContent();

      case ContentType.DRAWING:
        return renderDrawingContent();

      case ContentType.TEXT:
        return renderTextContent();
      default:
        return null;
    }
  };

  return (
    <div className="h-[620px] max-w-[340px] w-[340px] mt-10 lg:mt-0 mx-auto first:mt-0">
      <Link href={`/post/${post.id}`}>
        <div
          aria-hidden
          className="relative h-full w-full max-w-full cursor-pointer"
        >
          <PostOverlay post={post} />
          {renderContent()}
        </div>
      </Link>
    </div>
  );
}
